<script setup>
    import { getConferenceTimezone, getConferenceTimezoneLabel, isArchivedSession, isSessionOnDemand } from '~~/shared/utilities/schedule';
    import { useScheduleAction } from '@/composables/useScheduleAction';
    import { useScheduleControls } from '@/composables/useScheduleControls';

    const { $clientPosthog } = useNuxtApp();
    const dayjs = useDayjs();

    const defaultSpeakerImg = '/assets/img/placeholder-speaker.jpg';

    const props = defineProps({
        session: {
            type: Object,
            required: true,
        },
        hideFeatured: {
            type: Boolean,
            default: false,
        },
        disableLink: {
            type: Boolean,
            default: false,
        },
        speakerSize: {
            type: Number,
            default: 64,
        },
        theme: {
            type: String,
            default: 'light', // 'light' (white bg) | 'dark' (light-purple bg)
            validator: (v) => ['light', 'dark'].includes(v),
        },
        clampDescription: {
            type: Boolean,
            default: true,
        },
        showSpeakerNames: {
            type: Boolean,
            default: false,
        },
        // Speaker tray and speaker page (Nick, Asana 1217803465946591): on
        // desktop there is room for everyone, so the "+N" collapse below only
        // applies under lg there.
        allSpeakersFromLg: {
            type: Boolean,
            default: false,
        },
        showSpeakerBorder: {
            type: Boolean,
            default: true,
        },
        useFullDescription: {
            type: Boolean,
            default: false,
        },
        // Opt-in: the speaker tray and speaker page want the same
        // "+ Add to My Schedule" button the other session cards have (UNB).
        // Off by default so grid-list's compact cards are unchanged.
        showAddButton: {
            type: Boolean,
            default: false,
        },
    });

    // Same flow every other card and the session sidebar use — enqueue,
    // conflict → doubleBook, refreshSession, seat reminder.
    const { busy, addButtonSaved, seatButtonSaved, seatButtonWaitlisted, handleAdd, handleSeat } =
        useScheduleAction(toRef(props, 'session'));
    const { showMySchedule, showSeatReservations } = useScheduleControls();

    const speakerSizePx = computed(() => `${props.speakerSize}px`);

    const isDark = computed(() => props.theme === 'dark');

    // FULL-SESSION COLOURING, mirrored from the agenda's detail-card so the two
    // read as the same card in two sizes (Nick, Asana 1217802085538908: "follow
    // the existing Session is Full cards that are implemented on the full
    // agenda"). `isFull` already drove the header LABEL and the standby button;
    // it drove no colour at all, so a full session looked identical to an
    // ordinary one.
    //
    // detail-card splits full sessions three ways and colours them differently,
    // and the split matters here:
    //   full-session         — full, holding nothing: pink body, dark-pink stroke
    //   full-saved-seat      — full, seat or place held: ordinary body
    //   full-action-required — full, added but unclaimed: the orange body wins
    // Only the first takes the pink ground; all three take the dark-pink bar.
    const isFullCapacity = computed(() => (
        !isArchive.value && isLimitedCapacity.value && isFull.value
    ));
    // Nothing held and nothing owed — detail-card's 'full-session'.
    const isFullUnclaimed = computed(() => (
        isFullCapacity.value
        && !isOrange.value
        && !seatButtonSaved.value
        && !seatButtonWaitlisted.value
    ));

    const cardBgClass = computed(() => {
        if (isOrange.value) return 'bg-(--orange)';
        // --purple on dark rather than --pink: detail-card substitutes the
        // purple family wholesale on the dark page, where a pale pink body would
        // strand against it.
        if (isFullUnclaimed.value) return isDark.value ? 'bg-(--purple)' : 'bg-(--pink)';
        return isDark.value ? 'bg-[var(--light-purple)]' : 'bg-white';
    });
    const cardHoverShadowClass = computed(() => (isDark.value ? 'hover:shadow-[0_0_7.9px_0_#420126]' : 'hover:shadow-[0_0_7.9px_0_#820CA780]'));
    // Type legibility, as distinct from the card's ground. Both the purple and
    // the orange body need white text and pale decoration, so every colour that
    // exists for CONTRAST keys off this rather than off isDark — otherwise the
    // orange card kept magenta type on orange, which is what detail-card ran
    // into when its orange variants were added.
    // NOTE the time line below does not use this alone. lightOnDark answers "is
    // the type light on a dark ground", which is true of both the purple card and
    // the orange one — but the SHADE differs. #fcc3dc is picked for the purple;
    // on orange it is a pale pink on a mid orange and fails contrast, which is
    // what Nick reported (Asana 1217802085538896 / 1217803717613707). Anything
    // that is plain white on both grounds can keep using this as-is.
    const lightOnDark = computed(() => isDark.value || isOrange.value);

    // The gap drawn between overlapping speaker hexagons is the card's own
    // background showing through, so it has to follow the body colour. #D63D00
    // is the orange card's stroke, matching the agenda cards.
    const hexStroke = computed(() => {
        if (isOrange.value) return '#D63D00';
        if (isFullUnclaimed.value) return isDark.value ? '#820CA7' : '#FCC3DC';
        return isDark.value ? '#9800C8' : 'white';
    });

    const textColorClass = computed(() => (lightOnDark.value ? 'text-white' : 'text-[var(--magenta)]'));
    // The light divider is --pink, which disappears entirely on the --pink body
    // of a full card; it steps to --dark-pink there.
    const dividerClass = computed(() => {
        if (isDark.value) return 'border-[var(--bright-purple)] pt-[30px]';
        if (isFullUnclaimed.value) return 'border-[var(--dark-pink)] pt-[16px]';
        return 'border-[var(--pink)] pt-[16px]';
    });

    // Nick's collapse rule (UNB-682) is stated in CARD width: icons off below
    // 435px, text off below 400px. An inline-size container query resolves
    // against the CONTENT box, though, and unlike detail-card — whose padding
    // sits on inner elements — this card carries p-[40px] on the container
    // itself. So the same rule is 80px narrower here: a 452px card measured
    // 372px and collapsed both buttons to icons at a perfectly comfortable
    // width. 400-80 = 320, 435-80 = 355.
    const COLLAPSE = '@max-[320px]:size-[34px] @max-[320px]:p-0 @max-[320px]:gap-0 @max-[320px]:rounded-full';
    const ICON_BAND = '@min-[320px]:@max-[355px]:hidden';
    const LABEL_HIDE = '@max-[320px]:hidden';

    // Matches the sidebar and detail-card: filled magenta once added, going
    // orange on hover to signal that clicking again removes it.
    const addBtnClasses = computed(() => {
        // Same two-step collapse as detail-card (Nick, UNB-682) — the Speaker
        // Sessions lists render this card, so the rules have to hold here too.
        // Only one button sits in this footer rather than two, so nothing can
        // collide; the collapse is here to stop a long label ("Added to My
        // Schedule") from overrunning a narrow card.
        const base = 'group/addbtn inline-flex items-center justify-center gap-[8px] border rounded-[50px] px-[14px] py-[8px] cursor-pointer transition-colors duration-200 font-hubspot-serif text-[15px] leading-[1.2] tracking-[0.3px] disabled:opacity-60 ' + COLLAPSE;
        if (addButtonSaved.value) {
            return `${base} bg-(--magenta) border-(--magenta) text-white hover:bg-(--orange) hover:border-(--orange)`;
        }
        return isDark.value
            ? `${base} bg-transparent border-white text-white hover:bg-white hover:text-(--magenta)`
            : `${base} bg-transparent border-(--magenta) text-(--magenta) hover:bg-(--magenta) hover:text-white`;
    });

    const emit = defineEmits(['load', 'load_speaker']);

    // Every speaker, not the first five. The slice used to drop the rest without
    // a trace — from the hexagons AND from the names list beside them.
    const speakers = computed(() => props.session.speakers ?? []);

    // Past five speakers the row collapses to a "+N" hexagon, as on the agenda
    // cards (Nick, Asana 1217803465946591; Figma 6602:240212): as many faces as
    // the row has room for, up to five, then the chip counting the rest.
    //
    // "Room for" is a container query on the row's wrapper. Each hexagon adds
    // speakerSize - 12 (the -12px overlap), and the list carries 10px of right
    // padding, so s hexagons need s * (size - 12) + 10. Literal per-size tables
    // so Tailwind compiles them — 64 (grid-list) and 70 (the speaker tray and
    // page) are the only sizes in use. Any other size shows five and the chip.
    const SPEAKER_FACES_MAX = 5;
    // The hexagon outline, shared by the faces and the chip.
    const SPEAKER_HEX_BORDER = 'M35.5674 1.01367C37.4526 0.893016 39.2445 1.58286 40.834 2.41211C41.636 2.83054 42.4173 3.30166 43.1543 3.75098C43.9013 4.20639 44.5952 4.63456 45.2646 4.99902H45.2656C48.805 6.92729 52.8466 8.6651 56.3213 11.1074L56.4365 11.1885L56.5254 11.2988C57.039 11.9366 57.6183 12.6624 58.0547 13.5244L58.2324 13.9023L58.2617 13.9697L58.2812 14.041C60.4459 22.1029 62.6499 30.1861 64.7207 38.293C65.3745 40.8515 66.2391 43.7716 66.7559 46.4355C67.0479 47.9413 67.3073 49.8069 66.0947 51.4805C65.68 52.0528 64.8813 52.6108 64.0732 53.1006C63.2174 53.6193 62.1796 54.1604 61.1328 54.6729C59.0638 55.6858 56.8001 56.6617 55.832 57.1152C49.3181 60.1675 42.4264 63.5203 35.7607 66.2598C31.7933 67.89 28.8937 66.4574 25.6094 65.4258C21.7515 64.2142 16.9934 62.8295 13.21 61.2461C11.72 60.6225 10.6451 59.8028 9.85645 58.6943C9.08398 57.6087 8.63543 56.3133 8.27832 54.834C5.99087 45.3565 3.84139 35.4715 1.88281 25.9141C1.78791 25.451 1.68491 25.0043 1.58203 24.5645C1.48013 24.1288 1.37701 23.6925 1.28809 23.2725C1.11148 22.4383 0.973095 21.5895 1.00488 20.7598C1.03752 19.9086 1.24855 19.0724 1.76367 18.2754C2.27158 17.4895 3.03514 16.803 4.07715 16.1777C6.20151 14.9026 8.72192 13.7337 10.8496 12.6436C15.189 10.4194 19.6367 8.3063 23.9648 6.14062C24.6687 5.7884 25.5259 5.31604 26.4873 4.78809C27.4404 4.26467 28.4856 3.69266 29.5361 3.15918C30.5852 2.62643 31.6575 2.12271 32.6699 1.73828C33.6704 1.35838 34.6733 1.07099 35.5674 1.01367Z';
    const collapseSpeakers = computed(() => speakers.value.length > SPEAKER_FACES_MAX);
    const SPEAKER_FIT = {
        64: {
            faces: ['', '', 'hidden @min-[218px]/speakers:flex', 'hidden @min-[270px]/speakers:flex', 'hidden @min-[322px]/speakers:flex'],
            labels: [
                { faces: 2, cls: '@min-[218px]/speakers:hidden' },
                { faces: 3, cls: 'hidden @min-[218px]/speakers:inline @min-[270px]/speakers:hidden' },
                { faces: 4, cls: 'hidden @min-[270px]/speakers:inline @min-[322px]/speakers:hidden' },
                { faces: 5, cls: 'hidden @min-[322px]/speakers:inline' },
            ],
        },
        70: {
            faces: ['', '', 'hidden @min-[242px]/speakers:flex', 'hidden @min-[300px]/speakers:flex', 'hidden @min-[358px]/speakers:flex'],
            labels: [
                { faces: 2, cls: '@min-[242px]/speakers:hidden' },
                { faces: 3, cls: 'hidden @min-[242px]/speakers:inline @min-[300px]/speakers:hidden' },
                { faces: 4, cls: 'hidden @min-[300px]/speakers:inline @min-[358px]/speakers:hidden' },
                { faces: 5, cls: 'hidden @min-[358px]/speakers:inline' },
            ],
        },
    };
    const speakerFit = computed(() => SPEAKER_FIT[props.speakerSize] || {
        faces: ['', '', '', '', ''],
        labels: [{ faces: 5, cls: '' }],
    });
    const speakerFaceClass = (i) => {
        if (!collapseSpeakers.value) return '';
        const fit = i < SPEAKER_FACES_MAX ? speakerFit.value.faces[i] : 'hidden';
        if (!props.allSpeakersFromLg) return fit;
        return fit ? `${fit} lg:flex` : '';
    };
    // The chip is the speaker hexagon filled flat: #FCD4E5, or white on the pink
    // body of a full card, where the pink would vanish (Figma).
    const speakerChipFill = computed(() => (
        isFullUnclaimed.value && !isDark.value ? '#ffffff' : '#FCD4E5'
    ));

    const description = computed(() => {
        if (props.useFullDescription) {
            return props.session.description || props.session.short_description || '';
        }
        const short = props.session.short_description?.trim();
        return short || props.session.description || '';
    });

    const primarySponsor = computed(() => {
        return props.session.sponsors?.find((s) => s?.sponsor_logo?.length > 0) ?? null;
    });

    const sessionLink = computed(() => {
        const id = (props.session.unique_id || props.session.id || '').toLowerCase();
        return `/sessions/${id}`;
    });

    const isArchive = computed(() => isArchivedSession(props.session));
    const isLimitedCapacity = computed(() => props.session.session_reservations === true);

    // The same card states the agenda cards carry, applied as the colour shifts
    // this card already uses for Limited Capacity (Jack, 2026-08-22). Conflict is
    // a MODIFIER, not a variant: whatever the card was, a clash turns the header
    // and border error-red, prefixes ACTION NEEDED and adds the "!" — the body
    // colour is still decided by the underlying state.
    const isFull = computed(() => props.session.is_full === true);
    const seatActionRequired = computed(() => (
        showSeatReservations.value
        && isLimitedCapacity.value
        && addButtonSaved.value
        && !seatButtonSaved.value
        && !seatButtonWaitlisted.value
    ));
    const isConflicted = computed(() => props.session.is_conflicted === true);

    // Orange means "you need to do something about this one", exactly as on the
    // agenda. Archive sessions never take it — they cannot be acted on.
    const isOrange = computed(() => !isArchive.value && seatActionRequired.value);

    // A full session used to hide this — nothing to reserve. Bizzabo answers a
    // PUT on a full session with 202 and adds the attendee to its standby list
    // (verified live 2026-09-02), so the control is a real action there now and
    // reads "Join Waitlist". See the note in the agenda cards' hasTwoButtons.
    const showSeatButton = computed(() => (
        !isArchive.value
        && showSeatReservations.value
        && isLimitedCapacity.value
    ));

    // Filled once a seat is held — the state the button is how they SEE — and
    // outlined otherwise. On the orange body the outline goes white, since the
    // magenta stroke is barely legible there.
    const seatBtnClasses = computed(() => {
        const base = 'group/seatbtn inline-flex items-center justify-center gap-[8px] border rounded-[50px] px-[14px] py-[8px] cursor-pointer transition-colors duration-200 font-hubspot-serif text-[15px] leading-[1.2] tracking-[0.3px] disabled:opacity-60 ' + COLLAPSE;
        // Held seat hovers to orange, exactly as the Added button beside it does:
        // the click removes the seat, so the control takes the compliant orange
        // rather than the magenta it already wears (Jack, 2026-08-22).
        // A standby place is HELD, exactly as a seat is — pressing the button
        // gives it up. Without this it fell through to the UNCLAIMED styling
        // below, so it looked identical to a Save a Seat button nobody had
        // pressed, and nothing on it suggested it could be pressed to leave.
        if (seatButtonSaved.value || seatButtonWaitlisted.value) return `${base} bg-(--magenta) border-(--magenta) text-white hover:bg-(--orange) hover:border-(--orange) hover:text-white`;
        if (isOrange.value) return `${base} bg-transparent border-white text-white hover:bg-white hover:text-(--magenta)`;
        if (isDark.value) return `${base} bg-transparent border-white text-white hover:bg-white hover:text-(--magenta)`;
        return `${base} bg-transparent border-(--magenta) text-(--magenta) hover:bg-(--magenta) hover:text-white`;
    });

    // A full session cannot offer a seat, only a place in the queue — so the
    // seat button becomes a standby affordance there, label and icon together.
    // Limited (not full) sessions keep "Save a Seat" and the chair glyph.
    // This card has no hasSavedSeat prop; seatButtonSaved is the only source.
    const seatIsStandby = computed(() =>
        seatButtonWaitlisted.value || (isFull.value && !seatButtonSaved.value && isLimitedCapacity.value)
    );

    const baseHeaderLabel = computed(() => {
        if (!isLimitedCapacity.value) return '';
        // Appended, not gated on isFull: Bizzabo decides capacity, and is_full
        // can be half an hour behind it. See the agenda cards' note.
        const base = isFull.value ? 'Session is Full' : 'Limited Capacity';
        return seatButtonWaitlisted.value ? `${base} - Waitlisted` : base;
    });
    const headerLabel = computed(() => {
        if (!isConflicted.value) return baseHeaderLabel.value;
        return baseHeaderLabel.value ? `${baseHeaderLabel.value} - Action Needed` : 'Action Needed';
    });
    const showHeader = computed(() => !isArchive.value && !!headerLabel.value);
    // Error-red wins over the capacity magenta; the border only appears with it.
    // The dark-pink bar belongs to ALL THREE full variants, not just the
    // unclaimed one — it is what says "this session is full" at a glance.
    // #a058b6 on dark for the same reason the body swaps: see detail-card's
    // note, where --dark-pink on the purple page reads as near-black.
    const headerBgClass = computed(() => {
        if (isConflicted.value) return 'bg-[#980000]';
        if (isFullCapacity.value) return isDark.value ? 'bg-[#a058b6]' : 'bg-(--dark-pink)';
        return 'bg-(--magenta)';
    });
    // REPLACES the border rather than adding one: two border-colour utilities in
    // one class list leaves the winner to CSS source order (detail-card hit this).
    // Error red outranks the capacity stroke, as it does on the bar.
    const cardBorderClass = computed(() => {
        if (isConflicted.value) return 'border border-[#980000]';
        if (isFullUnclaimed.value) return isDark.value ? 'border border-[#c138f2]' : 'border border-(--dark-pink)';
        return '';
    });
    const sessionTypeName = computed(() => props.session.session_type?.[0]?.name || '');

    const formattedSessionTime = computed(() => {
        if (!props.session.start_date_and_time) return '';
        const tz = getConferenceTimezone(2026);
        const tzLabel = getConferenceTimezoneLabel(2026);
        const start = dayjs(props.session.start_date_and_time).tz(tz);
        const end = props.session.end_date_and_time ? dayjs(props.session.end_date_and_time).tz(tz) : null;
        const dayStr = start.format('ddd');
        const dateStr = start.format('MMM D');
        const startStr = start.format('h:mma');
        const endStr = end ? end.format('h:mma') : '';
        const timeRange = end ? `${startStr} – ${endStr} ${tzLabel}` : `${startStr} ${tzLabel}`;
        // Location omitted (date + time only); it lives on the session detail page/drawer.
        // nbsp + space each side of the bullet so two spaces render (HTML collapses
        // plain double-spaces to one).
        const sep = '  •  ';
        return `${dayStr}${sep}${dateStr}${sep}${timeRange}`;
    });

    const handleTitleClick = (event) => {
        $clientPosthog?.capture('session_clicked', {
            session_id: props.session.id,
            session_title: props.session.title,
        });
        if (props.disableLink) {
            event.preventDefault();
            emit('load', props.session);
        }
    };

    const handleSpeakerClick = (event, speaker) => {
        if (props.disableLink) {
            event.preventDefault();
            emit('load_speaker', speaker);
        }
    };
</script>

<template>
    <article class="compact-session-card @container group/card flex flex-col gap-[27px] p-[40px] rounded-[10px] h-full overflow-hidden transition-shadow duration-300"
        :class="[cardBgClass, cardHoverShadowClass, cardBorderClass]">
        <!-- Status bar across the top (2026 only) — full-bleed to the card edges
             via negative margins, matching the agenda session cards. Carries the
             capacity state, and turns error-red with the "!" when the session
             clashes with something else on My Schedule. -->
        <div v-if="showHeader"
            data-test="compact-header"
            class="-mx-[40px] -mt-[40px] flex items-center gap-[8px] h-[36px] px-[40px] shrink-0"
            :class="headerBgClass">
            <span v-if="isConflicted"
                aria-hidden="true"
                class="inline-flex items-center justify-center size-[15px] shrink-0 rounded-full bg-white font-hubspot-sans font-medium text-[11px] leading-none text-[#980000]">!</span>
            <span class="font-hubspot-sans font-medium text-[12px] leading-[1.5] tracking-[0.84px] uppercase text-white">
                {{ headerLabel }}
            </span>
        </div>
        <div class="flex flex-col gap-[24px]">
            <!-- The 860px reading cap sits on the TITLE and DESCRIPTION, not on
                 this column. It used to wrap the whole column, which also
                 constrained the date/time row — so the on-demand glyph's
                 justify-between right-aligned to 860px rather than to the card,
                 leaving it floating mid-card on the wide cards the speaker drawer
                 renders. Moving the cap to the two long-text elements keeps the
                 line length they need and lets the glyph reach the card edge,
                 without changing any gap or the order of anything. -->
            <div class="flex flex-col gap-[16px] w-full">
                <!-- Capacity/type pills (2026 only; archive sessions show none). The
                     date/time moved out of this block with the on-demand glyph —
                     see the note below. -->
                <template v-if="!isArchive">
                    <div v-if="sessionTypeName" class="flex flex-wrap gap-[8px] items-center">
                        <span v-if="sessionTypeName"
                            class="inline-flex items-center rounded-[25px] border px-[12px] py-[5px] font-hubspot-sans font-medium text-[11px] leading-[1] tracking-[0.77px] uppercase"
                            :class="lightOnDark ? 'border-white text-white' : 'border-(--dark-pink) text-(--dark-pink)'">
                            {{ sessionTypeName }}
                        </span>
                    </div>
                </template>

                <!-- Date/time, with the on-demand glyph pushed to its right.
                     Same placement the Calendar card uses (time left, glyph
                     right, justify-between); no mockup exists for this card, so
                     it follows the cards that do have one rather than inventing
                     a position.

                     DELIBERATELY OUTSIDE the !isArchive block above, while the
                     time itself stays inside it. Archived sessions show no
                     date/time — but they are precisely the sessions that carry
                     the on-demand flag today: all 37 livestream rows in the
                     catalog are 2025, and a past session with a recording is the
                     clearest case there is for the glyph. Placed inside the
                     archive gate it rendered for 2026 On Demand sessions and
                     vanished for every session that actually had a recording,
                     which is backwards.

                     The row renders when EITHER half has something to show, so a
                     session with no scheduled time still gets the glyph. -->
                <!-- justify-START when the glyph is alone, not justify-end. With a
                     time present the pair spreads (time left, glyph right); with no
                     time — every archived session — justify-end floated a lone glyph
                     against the card's right edge with nothing opposite it. A glyph
                     on its own sits flush left, matching the detail surfaces. -->
                <div v-if="(!isArchive && formattedSessionTime) || isSessionOnDemand(session)"
                    class="flex items-center gap-[8px] w-full"
                    :class="(!isArchive && formattedSessionTime) ? 'justify-between' : 'justify-start'">
                    <p v-if="!isArchive && formattedSessionTime"
                        class="font-hubspot-sans font-normal text-[13px] leading-[1.5] tracking-[0.195px] m-0"
                        :class="isOrange ? 'text-white' : (lightOnDark ? 'text-[#fcc3dc]' : 'text-(--dark-pink)')">
                        {{ formattedSessionTime }}
                    </p>
                    <!-- Inherits the time's colour treatment rather than carrying
                         its own, so the pair reads as one line on all three
                         grounds this card renders against. -->
                    <UIcon
                        v-if="isSessionOnDemand(session)"
                        name="local-on-demand"
                        aria-hidden="true"
                        class="shrink-0 w-[15px] h-[10px]"
                        :class="isOrange ? 'text-white' : (lightOnDark ? 'text-[#fcc3dc]' : 'text-(--dark-pink)')"
                    />
                </div>
                <p v-if="!hideFeatured && session.featured"
                    class="font-hubspot-sans font-medium text-[12px] leading-[1.5] tracking-[0.84px] uppercase text-[var(--dark-pink)] m-0">
                    Featured
                </p>
                <MessLink :href="sessionLink" :label="session.title" class="no-underline" @click="handleTitleClick">
                    <h3 class="font-hubspot-serif font-normal text-[30px] leading-[1.3] tracking-[0] m-0 max-w-[860px]
                        no-underline decoration-dotted decoration-[0.045em]
                        underline-offset-[2px]
                        [text-decoration-skip-ink:auto] [text-underline-position:from-font]
                        hover:underline"
                        :class="[textColorClass, lightOnDark ? 'decoration-[#fcd4e5]' : 'decoration-[var(--dark-pink)]']"
                        v-html="session.title" />
                </MessLink>
                <MessLink v-if="description" :href="sessionLink" :label="session.title" class="no-underline display-block" @click="handleTitleClick">
                    <div class="font-hubspot-sans font-light text-[15px] leading-[23px] tracking-[0.3px] m-0 max-w-[860px] speaker-bio-content
                        no-underline decoration-dotted decoration-[8.5%]
                        underline-offset-[2px]
                        [text-decoration-skip-ink:auto] [text-underline-position:from-font]
                        hover:underline"
                        :class="[
                            textColorClass,
                            clampDescription ? 'line-clamp-3' : '',
                            lightOnDark ? 'decoration-[#fcd4e5]' : 'decoration-[var(--dark-pink)]',
                        ]"
                        v-html="description"></div>
                </MessLink>
            </div>
            <div v-if="speakers.length"
                class="@container/speakers flex flex-wrap items-center gap-y-[16px] gap-x-[32px] w-full">
            <ul class="speaker-hex-row flex items-center pr-[10px] m-0 p-0 list-none shrink-0">
                <li v-for="(speaker, i) in speakers" :key="speaker.id || i"
                    class="speaker-hex relative shrink-0 -mr-[12px]"
                    :class="speakerFaceClass(i)"
                    :style="{ zIndex: speakers.length - i + 1, width: speakerSizePx, height: speakerSizePx }">
                    <MessLink :href="`/speakers/${speaker.slug || speaker.id}`"
                        class="speaker-hex-link group relative display-block overflow-hidden"
                        :style="{ width: speakerSizePx, height: speakerSizePx }"
                        :external="disableLink"
                        @click="(e) => handleSpeakerClick(e, speaker)">
                        <img :src="speaker.profile_image?.[0]?.url || defaultSpeakerImg"
                            :alt="`${speaker.name} profile photo`"
                            loading="lazy"
                            :width="speakerSize" :height="speakerSize"
                            :style="{ width: speakerSizePx, height: speakerSizePx }"
                            class="object-cover transition-transform duration-300 group-hover:scale-125" />
                        <svg v-if="showSpeakerBorder" viewBox="0 0 68 68" :width="speakerSize" :height="speakerSize" class="absolute inset-0 pointer-events-none" aria-hidden="true">
                            <path :d="SPEAKER_HEX_BORDER"
                                fill="none" :stroke="hexStroke" stroke-width="4" />
                        </svg>
                    </MessLink>
                </li>
                <!-- "+N": the rest of the speakers, as one more hexagon. Opens the
                     session, where every speaker is listed. -->
                <li v-if="collapseSpeakers"
                    class="speaker-hex relative shrink-0 -mr-[12px]"
                    :class="allSpeakersFromLg ? 'lg:hidden' : ''"
                    :style="{ zIndex: 1, width: speakerSizePx, height: speakerSizePx }">
                    <MessLink :href="sessionLink"
                        :label="`View all ${speakers.length} speakers`"
                        class="speaker-hex-link relative flex items-center justify-center no-underline"
                        :style="{ width: speakerSizePx, height: speakerSizePx, backgroundColor: speakerChipFill }"
                        :external="disableLink"
                        @click="handleTitleClick">
                        <svg v-if="showSpeakerBorder" viewBox="0 0 68 68" :width="speakerSize" :height="speakerSize" class="absolute inset-0 pointer-events-none" aria-hidden="true">
                            <path :d="SPEAKER_HEX_BORDER" fill="none" :stroke="hexStroke" stroke-width="4" />
                        </svg>
                        <span class="relative font-hubspot-sans font-normal text-[14px] leading-[23px] tracking-[0.28px] text-(--magenta)" aria-hidden="true">
                            <span v-for="label in speakerFit.labels" :key="label.faces" :class="label.cls">+{{ speakers.length - label.faces }}</span>
                        </span>
                    </MessLink>
                </li>
            </ul>
            <p v-if="showSpeakerNames"
                class="font-hubspot-serif font-normal text-[14px] leading-[1.4] tracking-[0.14px] m-0 flex-1"
                :class="textColorClass">
                <template v-for="(speaker, i) in speakers" :key="speaker.id || i">
                    <MessLink :href="`/speakers/${speaker.slug || speaker.id}`"
                        :external="disableLink"
                        class="no-underline decoration-dotted decoration-[3.5%]
                            underline-offset-[3px]
                            [text-decoration-skip-ink:auto] [text-underline-position:from-font]
                            hover:underline"
                        :class="[textColorClass, lightOnDark ? 'decoration-[#fcd4e5]' : 'decoration-[var(--dark-pink)]']"
                        @click="(e) => handleSpeakerClick(e, speaker)">
                        {{ speaker.name }}
                    </MessLink><span v-if="i < speakers.length - 1">, </span>
                </template>
            </p>
            </div>
        </div>
        <div v-if="showAddButton && showMySchedule" class="mt-auto flex flex-wrap items-center gap-[12px]">
            <button
                type="button"
                data-test="compact-add"
                :aria-label="addButtonSaved ? 'Added to My Schedule' : 'Add to My Schedule'"
                :disabled="busy"
                :class="addBtnClasses"
                @click.stop.prevent="handleAdd"
            >
                <Plus class="size-[11px] shrink-0 transition-transform duration-200 group-hover/addbtn:rotate-45 motion-reduce:transition-none" :class="ICON_BAND" />
                <span :class="LABEL_HIDE">
                    <template v-if="addButtonSaved">
                        <span class="group-hover/addbtn:hidden">Added to My Schedule</span>
                        <span class="hidden group-hover/addbtn:inline">Remove Session</span>
                    </template>
                    <template v-else>Add to My Schedule</template>
                </span>
            </button>

            <!-- Save a Seat, on the same terms as the agenda cards: only for
                 limited-capacity sessions once seat saving has opened, and never
                 on a full session nobody holds anything for — there is nothing to
                 reserve (Nick, Asana 1217708959827801 and 1217708959827816). -->
            <button
                v-if="showSeatButton"
                type="button"
                data-test="compact-seat"
                :aria-label="seatButtonWaitlisted ? 'Waitlisted' : seatButtonSaved ? 'Seat Saved' : (seatIsStandby ? 'Join Waitlist' : 'Save a Seat')"
                :disabled="busy"
                :class="seatBtnClasses"
                @click.stop.prevent="handleSeat"
            >
                <TimeCircle v-if="seatIsStandby" class="size-[14px] shrink-0" :class="ICON_BAND" />
                <Seat v-else class="w-[10.167px] h-[11.611px] shrink-0" :class="ICON_BAND" />
                <!-- Same shape as the Add button beside it: a named group and two
                     spans, so hovering states what the press will DO. Only the
                     standby label swaps for now — "Seat Saved" keeps its
                     colour-only affordance until that copy is agreed. -->
                <span v-if="seatButtonWaitlisted" :class="LABEL_HIDE">
                    <span class="group-hover/seatbtn:hidden">Waitlisted</span>
                    <span class="hidden group-hover/seatbtn:inline">Remove from Waitlist</span>
                </span>
                <span v-else :class="LABEL_HIDE">{{ seatButtonSaved ? 'Seat Saved' : (seatIsStandby ? 'Join Waitlist' : 'Save a Seat') }}</span>
            </button>
        </div>

        <div v-if="primarySponsor"
            class="border-t mt-auto"
            :class="dividerClass">
            <div class="w-[115px] h-[74px] bg-white rounded-[10px] flex items-center justify-center overflow-hidden">
                <MessImage :image="primarySponsor.sponsor_logo[0]"
                    :alt="`${primarySponsor.name} logo`"
                    class="max-w-[88px] max-h-[56px] object-contain" lazy />
            </div>
        </div>
    </article>
</template>

<style>
.compact-session-card .speaker-hex-link {
    mask-image: url('/img/2026/speakers/speaker-hex.svg');
    -webkit-mask-image: url('/img/2026/speakers/speaker-hex.svg');
    mask-size: 100% 100%;
    -webkit-mask-size: 100% 100%;
    mask-repeat: no-repeat;
    -webkit-mask-repeat: no-repeat;
}

/* Session description rich-text spacing */
.compact-session-card .speaker-bio-content :is(p, ul, ol):not(:last-child) {
    margin-bottom: 0.5rem;
}

/* Purple shard bullets for UL/OL inside session descriptions (matches blog) */
.compact-session-card .speaker-bio-content ul {
    list-style: none;
    padding-left: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.compact-session-card .speaker-bio-content ul li {
    display: flex;
    align-items: baseline;
    gap: 10px;
}

.compact-session-card .speaker-bio-content ul li::before {
    content: '';
    width: 5px;
    height: 6px;
    background: url('/img/2026/bullet-shard.svg') no-repeat center / contain;
    flex-shrink: 0;
    margin-top: 8px;
}

.compact-session-card .speaker-bio-content ol {
    padding-left: 17px;
    list-style: none;
    counter-reset: compact-ol;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.compact-session-card .speaker-bio-content ol li {
    counter-increment: compact-ol;
    position: relative;
}

.compact-session-card .speaker-bio-content ol li::before {
    content: counter(compact-ol) ".";
    color: var(--purple, #820CA7);
    position: absolute;
    left: -17px;
}
</style>

